import { useCallback, useMemo, useState } from "react";

import { useStateWithHistory } from ".";
import { useEditPlayMutation } from "../redux/slices";
import { Sport } from "../redux/slices/sport-slice";
import { BasketballCourtView, Frame, SportPlay } from "../types";

type UpdateFrameCallbackFunc = (frame: Frame) => Frame;
type UpdateFrameFunc = (frameCallback: UpdateFrameCallbackFunc | Frame, skipSave?: boolean) => void;

export const usePlayStateApi = <TPlay extends SportPlay>(play: TPlay) => {
  // /** Save Play Optimistically should be used internally on the individual play page, but not when Done is used to navigate back home */
  // // save play optimistically by writing current play state to the server
  // const [savePlayOptimisticallyFn, { isLoading: isSaving }] = useSavePlayOptimisticallyMutation();
  // const savePlayOptimistically = useCallback(
  //   async (play: SportPlay) => {
  //     await savePlayOptimisticallyFn({ play });
  //   },
  //   [savePlayOptimisticallyFn]
  // );

  const {
    editPlay,
    editPlayDebounced,
    editPlayProps: { isLoading: isSavingPlay },
  } = useEditPlayMutation();
  const savePlay = useCallback(
    async <TPlay extends SportPlay>(play: TPlay, fromDone = false) => {
      await editPlay({ play, fromDone });
    },
    [editPlay]
  );
  // initial playState with play
  const [playState, setPlayStateHistory, { undo: undoPlayState, redo: redoPlayState }] =
    useStateWithHistory<TPlay>(play);

  const setPlayState = useCallback(
    <TSportPlay extends TPlay>(payload: TSportPlay | ((prevState: TPlay) => TSportPlay)) => {
      setPlayStateHistory(payload);
    },
    [setPlayStateHistory]
  );

  // provide a way to update the playState with any new play properties
  const updatePartialPlay = <TPlay extends SportPlay>(newProps: Partial<TPlay>) => {
    setPlayState((prevState) => {
      const newPlayState = { ...prevState, ...newProps };
      savePlay(newPlayState);
      return newPlayState;
    });
  };

  // keep a state of the current active frame id, defaulting to the first play frame
  const [activeFrameId, setActiveFrameId] = useState<string>(playState.frames[0] ? playState.frames[0].id : "");

  // capture the current active frame when playFrames or activeFrameId updates
  const activeFrame = useMemo(
    () => playState.frames.find((playFrame) => playFrame.id === activeFrameId) || null,
    [playState, activeFrameId]
  );

  const activeFrameStencils = useMemo(() => activeFrame?.frameStencils || [], [activeFrame]);

  const updateActiveFrame: UpdateFrameFunc = useCallback(
    (frameCallback, skipSave = false) => {
      if (activeFrameId) {
        setPlayState((prevState) => {
          const frameToUpdate = prevState.frames.find((frame) => frame.id === activeFrameId);
          const index = prevState.frames.findIndex((frame) => frame.id === activeFrameId);

          let newState: TPlay;
          if (frameToUpdate) {
            const newFrame: Frame = frameCallback instanceof Function ? frameCallback(frameToUpdate) : frameCallback;

            newState = {
              ...prevState,
              frames: [...prevState.frames.slice(0, index), newFrame, ...prevState.frames.slice(index + 1)],
            };
          } else {
            newState = prevState;
          }

          if (!skipSave) editPlayDebounced({ play: newState, fromDone: false });
          return newState;
        });
      }
    },
    [activeFrameId, editPlayDebounced, setPlayState]
  );

  return {
    playState,
    setPlayState,
    updatePartialPlay,
    activeFrame,
    activeFrameStencils,
    updateActiveFrame,
    setActiveFrameId,
    undoPlayState,
    redoPlayState,
    // savePlayOptimistically,
    savePlay,
    isSavingPlay,
  };
};

export type PlayStateApi = ReturnType<typeof usePlayStateApi>;

export const DEFAULT_PLAY_STATE: SportPlay = {
  sport: Sport.BASKETBALL,
  id: "",
  drawId: "",
  name: "",
  createdTimestamp: "",
  updatedTimestamp: "",
  frames: [],
  tags: [],
  playSurfaceId: "",
  courtOptions: {},
  playSurfaceView: BasketballCourtView.FRONTCOURT,
  properties: { outOfBoundsEnd: false, outOfBoundsLeft: false, outOfBoundsRight: false },
  assets: [],
  recipients: [],
  orientation: 0,
};

export const DEFAULT_PLAY_STATE_API: PlayStateApi = {
  playState: DEFAULT_PLAY_STATE,
  setPlayState: () => undefined,
  updatePartialPlay: () => undefined,
  activeFrame: null,
  activeFrameStencils: [],
  updateActiveFrame: () => undefined,
  setActiveFrameId: () => undefined,
  undoPlayState: () => undefined,
  redoPlayState: () => undefined,
  // savePlayOptimistically: () => undefined,
  savePlay: () => Promise.resolve(),
  isSavingPlay: false,
};
